import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import { validateResponse } from "@/api/validateResponse";
import {
  GenerateInvoicePdfResultSchema,
  InvoiceListResponseSchema,
  InvoiceSchema,
  type CreateInvoiceInput,
  type GenerateInvoicePdfResult,
  type Invoice,
  type InvoiceKind,
  type InvoiceStatus,
  type PatchInvoiceInput,
} from "@/api/types/invoice";
import {
  PaymentListResponseSchema,
  PaymentSchema,
  type AddPaymentInput,
  type Payment,
} from "@/api/types/payment";

/** Query-key factory — mirrors the events hook's convention (cursor list + detail). */
export const invoiceKeys = {
  all: ["invoices"] as const,
  lists: () => [...invoiceKeys.all, "list"] as const,
  list: (params: { event_id?: string; status?: InvoiceStatus; kind?: InvoiceKind; limit?: number }) =>
    [...invoiceKeys.lists(), params] as const,
  details: () => [...invoiceKeys.all, "detail"] as const,
  detail: (id: string | undefined) => [...invoiceKeys.details(), id] as const,
  payments: (invoiceId: string | undefined) => [...invoiceKeys.all, "payments", invoiceId] as const,
};

function invalidateAll(queryClient: QueryClient) {
  void queryClient.invalidateQueries({ queryKey: invoiceKeys.all });
}

interface UseInvoicesParams {
  event_id?: string;
  status?: InvoiceStatus;
  kind?: InvoiceKind;
  limit?: number;
}

/** List query with cursor pagination, mirroring useEvents'/useContacts' convention. */
export function useInvoices(params: UseInvoicesParams = {}) {
  const { event_id, status, kind, limit = 25 } = params;

  return useInfiniteQuery({
    queryKey: invoiceKeys.list({ event_id, status, kind, limit }),
    queryFn: async ({ pageParam }) => {
      const searchParams = new URLSearchParams();
      if (event_id) searchParams.set("event_id", event_id);
      if (status) searchParams.set("status", status);
      if (kind) searchParams.set("kind", kind);
      searchParams.set("limit", String(limit));
      if (pageParam) searchParams.set("cursor", pageParam);

      const { data } = await apiClient.get(`/v2/invoices?${searchParams.toString()}`);
      return validateResponse(InvoiceListResponseSchema, data, "GET /v2/invoices");
    },
    initialPageParam: "" as string,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
    staleTime: 15 * 1000,
  });
}

export function useInvoice(invoiceId: string | undefined) {
  return useQuery<Invoice>({
    queryKey: invoiceKeys.detail(invoiceId),
    queryFn: async () => {
      const { data } = await apiClient.get(`/v2/invoices/${invoiceId}`);
      return validateResponse(InvoiceSchema, data, "GET /v2/invoices/:id");
    },
    enabled: !!invoiceId,
  });
}

export function useCreateInvoice() {
  const queryClient = useQueryClient();
  return useMutation<Invoice, unknown, CreateInvoiceInput>({
    mutationFn: async (input) => {
      const { data } = await apiClient.post("/v2/invoices", input);
      return validateResponse(InvoiceSchema, data, "POST /v2/invoices");
    },
    onSuccess: (invoice) => {
      queryClient.setQueryData(invoiceKeys.detail(invoice.id), invoice);
      invalidateAll(queryClient);
    },
  });
}

export function usePatchInvoice() {
  const queryClient = useQueryClient();
  return useMutation<Invoice, unknown, PatchInvoiceInput>({
    mutationFn: async ({ id, ...patch }) => {
      const { data } = await apiClient.patch(`/v2/invoices/${id}`, patch);
      return validateResponse(InvoiceSchema, data, "PATCH /v2/invoices/:id");
    },
    onSuccess: (invoice) => {
      queryClient.setQueryData(invoiceKeys.detail(invoice.id), invoice);
      invalidateAll(queryClient);
    },
  });
}

function useInvoiceAction(endpoint: string, label: string) {
  const queryClient = useQueryClient();
  return useMutation<Invoice, unknown, { id: string; body?: Record<string, unknown> }>({
    mutationFn: async ({ id, body }) => {
      const { data } = await apiClient.post(`/v2/invoices/${id}/${endpoint}`, body ?? {});
      return validateResponse(InvoiceSchema, data, `POST /v2/invoices/:id/${label}`);
    },
    onSuccess: (invoice) => {
      queryClient.setQueryData(invoiceKeys.detail(invoice.id), invoice);
      invalidateAll(queryClient);
    },
  });
}

export function useSendInvoice() {
  return useInvoiceAction("send", "send");
}

export function useVoidInvoice() {
  return useInvoiceAction("void", "void");
}

/**
 * Generates the invoice PDF. Contract: "returns {pdf_key} or {url}" — mirrors
 * the menu/shopping-list PDF convention (useGenerateMenuPdf): prefer an
 * explicit `url`, fall back to a GET on the invoice's own path when only a
 * bare `pdf_key` comes back (see InvoiceDetailPage).
 */
export function useGenerateInvoicePdf() {
  return useMutation<GenerateInvoicePdfResult, unknown, string>({
    mutationFn: async (invoiceId) => {
      const { data } = await apiClient.post(`/v2/invoices/${invoiceId}/pdf`, {});
      return validateResponse(
        GenerateInvoicePdfResultSchema,
        data,
        "POST /v2/invoices/:id/pdf",
      );
    },
  });
}

// ---- Payments -------------------------------------------------------------

export function usePayments(invoiceId: string | undefined) {
  return useQuery<Payment[]>({
    queryKey: invoiceKeys.payments(invoiceId),
    queryFn: async () => {
      const { data } = await apiClient.get(`/v2/invoices/${invoiceId}/payments`);
      return validateResponse(
        PaymentListResponseSchema,
        data,
        "GET /v2/invoices/:id/payments",
      ).items;
    },
    enabled: !!invoiceId,
    staleTime: 10 * 1000,
  });
}

/**
 * Recording a payment can flip the parent invoice's status (partial/paid) —
 * invalidate both the payments list and the invoice detail/list so the
 * status badge and paid-amount everywhere reconcile.
 */
export function useAddPayment() {
  const queryClient = useQueryClient();
  return useMutation<Payment, unknown, AddPaymentInput>({
    mutationFn: async ({ invoiceId, ...body }) => {
      const { data } = await apiClient.post(`/v2/invoices/${invoiceId}/payments`, body);
      return validateResponse(PaymentSchema, data, "POST /v2/invoices/:id/payments");
    },
    onSuccess: (_payment, variables) => {
      void queryClient.invalidateQueries({ queryKey: invoiceKeys.payments(variables.invoiceId) });
      void queryClient.invalidateQueries({ queryKey: invoiceKeys.detail(variables.invoiceId) });
      invalidateAll(queryClient);
    },
  });
}
